import type { AssetMediaRef } from "./index";

type JsonRecord = Record<string, unknown>;

/**
 * The string fields of `AssetMediaRef` besides `id` and `kind`: the one list
 * every reader of a media ref's shape derives from (the operations parser, the
 * accepted-artifact contract). A writer that stores any other field on a media
 * ref writes outside the contract (QA4 A2 #7: a URL capture's `sourceUrl` and
 * `metadata` made every later Save of that page a 400).
 */
export const ASSET_MEDIA_REF_STRING_FIELDS = [
  "fileId",
  "filePath",
  "bucket",
  "objectKey",
  "gcsUri",
  "publicUrl",
  "mimeType",
  "url",
  "altText",
  "label",
  "sliceOf",
  "sliceRole",
  "captureEndedOn",
] as const;

/**
 * The non-negative integer fields of `AssetMediaRef`: a slice's position, and
 * the number of whole screens a page's top-of-page capture holds (written only
 * by the Save's capture). The same readers derive from it.
 */
export const ASSET_MEDIA_REF_INTEGER_FIELDS = ["sliceIndex", "capturedScreens"] as const;

type MediaStringField = (typeof ASSET_MEDIA_REF_STRING_FIELDS)[number];
type MediaIntegerField = (typeof ASSET_MEDIA_REF_INTEGER_FIELDS)[number];
type UnlistedMediaField = Exclude<
  keyof AssetMediaRef,
  MediaStringField | MediaIntegerField | "id" | "kind"
>;
const _MEDIA_FIELDS_EXHAUSTIVE: [UnlistedMediaField] extends [never]
  ? true
  : "ASSET_MEDIA_REF_STRING_FIELDS and ASSET_MEDIA_REF_INTEGER_FIELDS must list every field of AssetMediaRef" =
  true;
void _MEDIA_FIELDS_EXHAUSTIVE;

const isRecord = (value: unknown): value is JsonRecord =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);
const nonBlank = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

/**
 * Whether a non-image media record names bytes or a URL that downstream
 * delivery can resolve. Images have the stricter GCS-only predicate owned by
 * the image ingestion path; this predicate owns video/document/url-snapshot
 * locators shared by intake, explicit asset updates, and accepted state.
 */
export function hasUsableNonImageAssetMediaLocator(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    nonBlank(value.fileId) ||
    nonBlank(value.filePath) ||
    nonBlank(value.url) ||
    nonBlank(value.publicUrl) ||
    nonBlank(value.gcsUri) ||
    (nonBlank(value.bucket) && nonBlank(value.objectKey))
  );
}

/**
 * The stored file ids on a media list: the one reading of a ref's `fileId`
 * (a blank or missing one holds no file).
 */
export function mediaFileIds(media: unknown): Set<string> {
  const held = new Set<string>();
  for (const ref of Array.isArray(media) ? media : []) {
    if (isRecord(ref) && nonBlank(ref.fileId)) held.add(ref.fileId);
  }
  return held;
}

/**
 * The stored file ids a draft's tested assets hold on their media: the one
 * answer to "is this file already on an asset?". Every reader of that fact
 * (the card that offers a file, the files a Build's PLAN is shown, the
 * collect that adds an asset for a file) reads this, so a file sits on one
 * asset and is offered, shown and added once.
 */
export function assetFileIds(
  assets: ReadonlyArray<unknown> | null | undefined,
): Set<string> {
  const held = new Set<string>();
  for (const asset of assets ?? []) {
    if (!isRecord(asset)) continue;
    for (const id of mediaFileIds(asset.media)) held.add(id);
  }
  return held;
}
